import {
  Box,
  createTheme,
  CssBaseline,
  GlobalStyles,
  Stack,
  ThemeProvider,
  useMediaQuery,
} from "@mui/material";
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import AppTree from "./AppTree";
import Footer from "./Footer";
import Sidebar from "./Sidebar";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import AppButtons from "./AppButtons";
import { pages } from "../pages/pages";
import usePageTracking from "../hooks/usePageTracking";
import { isBrowser } from "react-device-detect";
import {
  ColorModeContext,
  getDesignTokens,
  getInitialMode,
  THEME_STORAGE_KEY,
  type ThemeMode,
} from "../theme/theme";

const Home = lazy(() => import("../pages/Home"));
const Literature = lazy(() => import("../pages/Literature"));
const MDContainer = lazy(() => import("../components/MDContainer"));
const NoteContainer = lazy(() => import("../components/NoteContainer"));

interface Page {
  index: number;
  name: string;
  route: string;
}

function initVisiblePageIndexs(pages: Page[]) {
  const tabs = [];
  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    tabs.push(page.index);
  }
  return tabs;
}

export default function App() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [expanded, setExpanded] = useState(isBrowser && !isMobile);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [currentComponent, setCurrentComponent] = useState("");
  const [visiblePageIndexs, setVisiblePageIndexs] = useState(
    initVisiblePageIndexs(pages)
  );
  const [visiblePages, setVisiblePages] = useState(pages);
  usePageTracking();

  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  const colorMode = useMemo(
    () => ({
      mode,
      toggle: () =>
        setMode((prev) => (prev === "dark" ? "light" : "dark")),
    }),
    [mode]
  );

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    } catch (e) {
      /* localStorage may be unavailable */
    }
    const root = document.documentElement;
    root.style.colorScheme = mode;
    root.style.backgroundColor = mode === "light" ? "#ffffff" : "#000000";
    root.setAttribute("data-theme", mode);
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        "content",
        mode === "light" ? "#ffffff" : "#0a0a0a"
      );
    }
  }, [mode]);

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  // On phones the explorer opens as an overlay drawer; close it once a file
  // is picked so it doesn't sit on top of the freshly opened page.
  const handleTreeItemSelected = useCallback(() => {
    if (isMobile) setExpanded(false);
  }, [isMobile]);

  const deletedIndex = visiblePages.find(
    (x) => !visiblePageIndexs.includes(x.index)
  )?.index;

  useEffect(() => {
    const newPages = [];

    for (const index of visiblePageIndexs) {
      const page = pages.find((x) => x.index === index);
      if (page) newPages.push(page);
    }
    setVisiblePages(newPages);

    if (visiblePageIndexs.length === 0) {
      setSelectedIndex(-1);
      navigate("/");
    } else if (
      deletedIndex === selectedIndex &&
      deletedIndex > Math.max(...visiblePageIndexs)
    ) {
      setSelectedIndex(Math.max(...visiblePageIndexs));
      const page = pages.find(
        (x) => x.index === Math.max(...visiblePageIndexs)
      );
      if (page) navigate(page.route);
    } else if (
      deletedIndex === selectedIndex &&
      deletedIndex < Math.max(...visiblePageIndexs)
    ) {
      setSelectedIndex(Math.min(...visiblePageIndexs));
      const page = pages.find(
        (x) => x.index === Math.min(...visiblePageIndexs)
      );
      if (page) navigate(page.route);
    } else {
    }
  }, [visiblePageIndexs, navigate, deletedIndex, selectedIndex]);

  return (
    <ColorModeContext.Provider value={colorMode}>
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      {/* Lock the document itself: the app shell below is exactly one viewport
          tall and owns all scrolling, so the body never competes with the
          content scroller (double scroll / rubber-band jank on mobile). */}
      <GlobalStyles
        styles={{
          html: { height: "100%" },
          body: {
            height: "100%",
            overflow: "hidden",
            overscrollBehavior: "none",
          },
          "#root": { height: "100%" },
        }}
      />
      <Box
        sx={{
          // Viewport-height fallbacks: vh → -webkit-fill-available (older
          // iOS) → dvh, so the shell matches the *visible* viewport instead
          // of hiding the footer behind the browser URL bar.
          height: "100vh",
          "@supports (-webkit-touch-callout: none)": {
            height: "-webkit-fill-available",
          },
          "@supports (height: 100dvh)": {
            height: "100dvh",
          },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          backgroundColor: "transparent",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", flexGrow: 1, minHeight: 0, position: "relative" }}>
          <Box sx={{ width: isMobile ? 45 : 50, flexShrink: 0 }}>
            <Sidebar
              setExpanded={setExpanded}
              expanded={expanded}
            />
          </Box>
          {expanded && isMobile && (
            <Box
              onClick={() => setExpanded(false)}
              sx={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 45,
                right: 0,
                zIndex: 15,
                backgroundColor: "rgba(0,0,0,0.45)",
              }}
            />
          )}
          {expanded && (
            <Box
              sx={{
                backgroundColor: "vscode.explorerBg",
                width: isMobile ? 200 : 220,
                flexShrink: 0,
                overflowY: "auto",
                // On phones the explorer floats over the content as a drawer
                // instead of squeezing the page into a sliver.
                ...(isMobile && {
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 45,
                  zIndex: 20,
                  boxShadow: "8px 0 24px rgba(0,0,0,0.35)",
                }),
              }}
            >
              <Stack sx={{ mt: 1 }}>
                <AppTree
                  pages={pages}
                  selectedIndex={selectedIndex}
                  setSelectedIndex={setSelectedIndex}
                  currentComponent={currentComponent}
                  setCurrentComponent={setCurrentComponent}
                  visiblePageIndexs={visiblePageIndexs}
                  setVisiblePageIndexs={setVisiblePageIndexs}
                  onItemSelected={handleTreeItemSelected}
                />
              </Stack>
            </Box>
          )}

          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ height: "33px", flexShrink: 0 }}>
              <AppButtons
                // pages={pages}
                pages={visiblePages}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                currentComponent={currentComponent}
                setCurrentComponent={setCurrentComponent}
                visiblePageIndexs={visiblePageIndexs}
                setVisiblePageIndexs={setVisiblePageIndexs}
              />
            </Box>

            <Box
              sx={{
                scrollBehavior: "smooth",
                flexGrow: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                // Keep flings inside the content pane from chaining into the
                // page (pull-to-refresh / rubber-band) on touch devices.
                overscrollBehaviorY: "contain",
              }}
            >
              <Suspense
                fallback={
                  <Stack
                    sx={{ height: "100%", py: 4 }}
                    alignItems="center"
                    justifyContent="center"
                  >
                    Loading...
                  </Stack>
                }
              >
                <Routes>
                  <Route
                    path="/"
                    element={<Home setSelectedIndex={setSelectedIndex} />}
                  />
                  {pages.map(({ index, name, route }) => (
                    <Route
                      key={index}
                      path={route}
                      element={
                        name === "literature.md" ? (
                          <Literature />
                        ) : (
                          <MDContainer path={`/pages/${name}`} />
                        )
                      }
                    />
                  ))}
                  <Route
                    path="/notes/:noteId"
                    element={<NoteContainer />}
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </Box>
          </Box>
        </Box>
        <Footer />
      </Box>
      {/* </Router> */}
    </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
